import { api, ApiError } from '../../lib/api';
import type {
  AssetLabel,
  Campaign,
  CampaignOfferFacts,
  CampaignStatus,
  GoogleAssets,
  LandingPageOverride,
  LandingPageType,
  MetaAdCopy,
  UtmConfig,
} from '../../types/campaign';
import type {
  CampaignCopyRefineRequest,
  CampaignCopyRefineResult,
  CampaignCopyRequest,
  CampaignCopyResult,
  OfferDistilRequest,
  OfferDistilResult,
} from '../../lib/campaignCopy/requests';

export type CampaignCreatePayload = {
  // Multi-share (Pre-P2b-3 §5). createdInBrandAccountId is the
  // organisational owner; shareTargets controls visibility (server
  // requires min length 1).
  createdInBrandAccountId: string;
  shareTargets: Array<{ accountId: string }>;
  name: string;
  slug?: string;
  description: string | null;
  // Consolidation §1.3: offer URLs moved to the Plan — not a Campaign field.
  landingPageType: LandingPageType;
  customLandingPageUrl: string | null;
  metaAdCopy: MetaAdCopy;
  googleAssets: GoogleAssets;
  googleLandingPageOverride: LandingPageOverride;
  metaLandingPageOverride: LandingPageOverride;
  metaAssetsFolderUrl: string | null;
  // P2c §4.1 + §5.3: per-Campaign Meta asset labels authored in the
  // Campaign editor's Assets section. Optional on the wire — server
  // defaults to [] when omitted (e.g. brand-skeleton create path).
  metaAssetLabels?: AssetLabel[];
  googleAssetsFolderUrl: string | null;
  otherAssetsFolderUrl: string | null;
  utmConfig: UtmConfig;
  // Pre-P2b-3 §1. Both optional on the wire — server defaults to null when
  // the caller omits them.
  campaignDescriptor?: string | null;
  metaCampaignNameTemplate?: string | null;
  // Pre-P2b-3 polish round 5 §2.2: Google ad-label template, parallel
  // structure to metaCampaignNameTemplate.
  googleAdLabelTemplate?: string | null;
  // ⚠️ The distilled offer mechanics (2026-08-27) — read once, edited by the AM,
  // and persisted through this ordinary audited save rather than by the copy
  // service, which owns no collection and writes nothing.
  offerFacts?: CampaignOfferFacts | null;
};

// PATCH endpoint — every field optional. createdInBrandAccountId is
// immutable post-create (slug uniqueness scope), so it's omitted.
// shareTargets IS editable on PATCH (Visibility section).
export type CampaignUpdatePayload = Partial<Omit<CampaignCreatePayload, 'createdInBrandAccountId'>>;

export type CampaignDefaults = {
  landingPageType: LandingPageType;
  customLandingPageUrl: string | null;
  metaAdCopy: MetaAdCopy;
  googleAssets: GoogleAssets;
  googleLandingPageOverride: LandingPageOverride;
  metaLandingPageOverride: LandingPageOverride;
  metaAssetsFolderUrl: string | null;
  googleAssetsFolderUrl: string | null;
  otherAssetsFolderUrl: string | null;
  utmConfig: UtmConfig;
};

async function maybe<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// Pre-P2b-3 §5: three filter modes.
//   filter='visibility' (default) — every Campaign visible to the brand
//     context (shareTargets walk up to or include the brand). Used by
//     the rollout Campaign dropdown and the brand-scoped CampaignList on
//     AccountDetail.
//   filter='owner' — strict organisational ownership (the brand is the
//     Campaign's createdInBrandAccountId). Used by designation lookups
//     and admin-context filtering.
//   filter='directlySharedWith' — Campaigns whose shareTargets[]
//     includes the given accountId verbatim (no walk-up, no walk-down).
//     Used by the Campaigns section on non-brand AccountDetail pages
//     (Pre-P2b-3 polish Fix 3). Despite the legacy `brandAccountId`
//     param name, the value can be any account type when this filter
//     is active.
export async function listCampaigns(options: {
  brandAccountId?: string;
  filter?: 'visibility' | 'owner' | 'directlySharedWith';
} = {}): Promise<Campaign[]> {
  const params = new URLSearchParams();
  if (options.brandAccountId) params.set('brandAccountId', options.brandAccountId);
  if (options.filter) params.set('filter', options.filter);
  const qs = params.size > 0 ? `?${params.toString()}` : '';
  const res = await api<{ campaigns: Campaign[] }>(`/api/campaigns${qs}`);
  return res.campaigns;
}

export async function getCampaign(id: string): Promise<Campaign | null> {
  return maybe(async () => {
    const res = await api<{ campaign: Campaign }>(`/api/campaigns/${id}`);
    return res.campaign;
  });
}

export async function getCampaignDefaults(
  brandAccountId: string,
  landingPageType: LandingPageType,
): Promise<CampaignDefaults> {
  const qs = `?brandAccountId=${encodeURIComponent(brandAccountId)}&landingPageType=${encodeURIComponent(landingPageType)}`;
  const res = await api<{ defaults: CampaignDefaults }>(`/api/campaigns/defaults${qs}`);
  return res.defaults;
}

export async function createCampaign(payload: CampaignCreatePayload): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>('/api/campaigns', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.campaign;
}

export async function updateCampaign(id: string, patch: CampaignUpdatePayload): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>(`/api/campaigns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return res.campaign;
}

export async function changeCampaignStatus(id: string, status: CampaignStatus): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>(`/api/campaigns/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
  return res.campaign;
}

export async function designateAlternate(id: string): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>(`/api/campaigns/${id}/designate-alternate`, {
    method: 'POST',
    body: '{}',
  });
  return res.campaign;
}

export async function designateOptOut(id: string): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>(`/api/campaigns/${id}/designate-optout`, {
    method: 'POST',
    body: '{}',
  });
  return res.campaign;
}

export async function clearAlternate(id: string): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>(`/api/campaigns/${id}/clear-alternate`, {
    method: 'POST',
    body: '{}',
  });
  return res.campaign;
}

export async function clearOptOut(id: string): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>(`/api/campaigns/${id}/clear-optout`, {
    method: 'POST',
    body: '{}',
  });
  return res.campaign;
}

export async function deleteCampaign(id: string): Promise<void> {
  await api<{ ok: true }>(`/api/campaigns/${id}`, { method: 'DELETE' });
}

// Stage 1 §13.15. Fire-and-forget stub-write for the Campaign editor's
// Meta Assets browser. Called once per folder-load per session when
// the Drive listing arrives. Server merges idempotently — a second
// call with the same stubs is a no-op short-circuit (no write, no
// audit).
export async function addCampaignAssetLabelStubs(
  id: string,
  stubs: AssetLabel[],
): Promise<Campaign> {
  const res = await api<{ campaign: Campaign }>(
    `/api/campaigns/${id}/asset-label-stubs`,
    { method: 'POST', body: JSON.stringify({ stubs }) },
  );
  return res.campaign;
}

// Stage 1 §13.17. Orphan AssetLabel prune for the Campaign's Meta
// Assets folder. Two calls:
//   - getOrphanLabelImpact (preview counts for the confirmation modal)
//   - pruneOrphanCampaignLabels (perform prune + cascade)
//
// The server identifies orphans against the live Drive listing,
// prunes them from the Campaign, and cascades to active/draft
// rollouts that reference this Campaign. Archived rollouts are
// preserved as historical.
export type OrphanLabelImpact = {
  hasFolder: boolean;
  campaignOrphanCount: number;
  affectedRowCount: number;
  affectedRolloutCount: number;
  historicalRowsSkipped: number;
};

export async function getOrphanLabelImpact(
  campaignId: string,
): Promise<OrphanLabelImpact> {
  const res = await api<{ impact: OrphanLabelImpact }>(
    `/api/campaigns/${campaignId}/orphan-label-impact`,
  );
  return res.impact;
}

export type OrphanPruneResult = {
  campaignOrphansPruned: number;
  rowsPruned: number;
  rolloutsAffected: number;
  historicalRowsSkipped: number;
};

export async function pruneOrphanCampaignLabels(
  campaignId: string,
): Promise<OrphanPruneResult> {
  const res = await api<{ result: OrphanPruneResult }>(
    `/api/campaigns/${campaignId}/prune-orphan-labels`,
    { method: 'POST', body: '{}' },
  );
  return res.result;
}

// ---------------------------------------------------------------------------
// AI copy generation (2026-08-26)
// ---------------------------------------------------------------------------
//
// ⚠️ ON-DEMAND ONLY. Both of these fire from an explicit click in the copy
// workspace — never from a page load, a form change or a query. Pinned by the
// source-shape guard campaignCopyOnDemandOnly.test.ts.

export async function generateCampaignCopy(
  payload: CampaignCopyRequest,
): Promise<CampaignCopyResult> {
  return api<CampaignCopyResult>('/api/campaigns/copy', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function refineCampaignCopy(
  payload: CampaignCopyRefineRequest,
): Promise<CampaignCopyRefineResult> {
  return api<CampaignCopyRefineResult>('/api/campaigns/copy/refine', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Read the offer PDFs once into the editable facts block.
 *
 * ⚠️ ON-DEMAND ONLY, and more sharply than the others: this is the call that
 * costs 58,714 input tokens on a 30-page toolkit. Two callers, both an explicit
 * click — "Read the offer material", and the FIRST generation of a campaign with
 * no block yet, which chains it so the cheap path is the default rather than an
 * opt-in nobody is prompted to take. Never from an effect, a mount or a link
 * change.
 */
export async function distilOfferFacts(
  payload: OfferDistilRequest,
): Promise<OfferDistilResult> {
  return api<OfferDistilResult>('/api/campaigns/copy/distil-offer', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
